import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '../SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : null;

const PerspectivesIndex = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/perspectives`)
      .then(r => r.json())
      .then(d => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black text-white">
      <SEO
        title="Perspectives"
        description="Long-form writing from MyInvestFolio on portfolio infrastructure, privacy, independence and long-term capital."
        path="/perspectives"
      />
      <header className="border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            <span className="text-xl font-bold">My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span></span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-3xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-emerald-600">Perspectives</h1>
          <div className="w-24 h-1 mx-auto bg-linear-to-r from-emerald-500 to-blue-500 mb-6"></div>
          <p className="text-base text-gray-300 max-w-xl mx-auto leading-relaxed">
            Long-form writing on portfolio infrastructure, privacy, independence and the architecture of serious wealth.
          </p>
        </div>

        {loading && <div className="text-center text-gray-400 py-12">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="text-center bg-gray-800/60 border border-dashed border-gray-700 rounded-xl p-16" data-testid="perspectives-empty">
            <p className="text-gray-300 text-lg mb-2">New perspectives are published periodically.</p>
            <p className="text-sm text-gray-500">
              The first pieces are being written. In the meantime, you can follow the founder on{' '}
              <a href="https://www.linkedin.com/in/rasheed-olaoluwa-82588933" target="_blank" rel="noopener noreferrer"
                className="text-emerald-400 underline hover:text-emerald-300">
                LinkedIn
              </a>.
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-6" data-testid="perspectives-list">
            {items.map((a) => (
              <button
                key={a.slug}
                onClick={() => navigate(`/perspectives/${a.slug}`)}
                className="w-full text-left bg-gray-800 hover:bg-gray-750 border border-gray-700 hover:border-emerald-500 rounded-xl p-7 transition group"
                data-testid={`perspective-card-${a.slug}`}
              >
                <h2 className="text-2xl font-bold text-white group-hover:text-emerald-400 transition mb-2">{a.title}</h2>
                {a.summary && <p className="text-base text-gray-300 mb-4 leading-relaxed">{a.summary}</p>}
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  {a.date && (
                    <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{formatDate(a.date)}</span>
                  )}
                  {a.reading_time && (
                    <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{a.reading_time} min read</span>
                  )}
                  {a.linkedin_url && (
                    <span className="flex items-center gap-1.5 text-emerald-400"><ExternalLink className="w-3 h-3" />Also on LinkedIn</span>
                  )}
                  <span className="ml-auto flex items-center gap-1 text-emerald-400 opacity-0 group-hover:opacity-100 transition">
                    Read <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PerspectivesIndex;
